/* eslint no-console: ["error", { allow: ["warn", "error", "log"] }] */

import { useEffect, useState } from 'react';

import { CaretRightIcon, ChartPolarIcon, GraduationCapIcon } from '@phosphor-icons/react';
import { Link, useLocation } from 'react-router-dom';

import { AIChatModal } from '@widgets/AIChatModal';

import { ROUTES } from '@shared/config/routes';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@shared/ui/collapsible';
import { CortexaLogo } from '@shared/ui/cortexa-logo';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@shared/ui/sidebar';

import { AppSidebarAIChatButton } from './AppSidebarAIChatButton';
import { AppSidebarOnboarding } from './AppSidebarOnboarding';

interface SidebarSubItem {
  title: string;
  url: string;
}

interface SidebarItem {
  title: string;
  url: string;
  icon: typeof GraduationCapIcon;
  items?: SidebarSubItem[];
}

const items: SidebarItem[] = [
  {
    title: 'GMAT Prep',
    url: ROUTES.DASHBOARD,
    icon: GraduationCapIcon,
    items: [
      {
        title: 'Quantitative reasoning',
        url: ROUTES.QUANT,
      },
      {
        title: 'Verbal reasoning',
        url: ROUTES.VERBAL,
      },
      {
        title: 'Data insights',
        url: ROUTES.DATA,
      },
      {
        title: 'Recent tests',
        url: '/recent_tests',
      },
    ],
  },
  {
    title: 'Analytics',
    url: '/analytics',
    icon: ChartPolarIcon,
  },
];

const getStartedItems = [
  {
    id: 'what-is-cortexa',
    label: 'What is Cortexa?',
    onClick: () => {
      console.log('What is Cortexa clicked');
    },
  },
  {
    id: 'gmat-overview',
    label: 'GMAT Focus Overview',
    onClick: () => {
      console.log('GMAT Overview clicked');
    },
  },
  {
    id: 'success-strategies',
    label: 'Success Strategies',
    onClick: () => {
      console.log('Success Strategies clicked');
    },
  },
];

const isRouteActive = (pathname: string, route: string) =>
  pathname === route || pathname.startsWith(`${route}/`);

const hasNestedItems = (item: SidebarItem): item is SidebarItem & { items: SidebarSubItem[] } =>
  Boolean(item.items?.length);

const isItemActive = (pathname: string, item: SidebarItem) =>
  isRouteActive(pathname, item.url) ||
  item.items?.some((subItem) => isRouteActive(pathname, subItem.url)) === true;

const isItemDirectlyActive = (pathname: string, item: SidebarItem) => pathname === item.url;

const getOpenGroups = (pathname: string) =>
  items.reduce<Partial<Record<string, boolean>>>((accumulator, item) => {
    if (!hasNestedItems(item)) {
      return accumulator;
    }

    accumulator[item.title] = isItemActive(pathname, item);
    return accumulator;
  }, {});

export function AppSidebar() {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [initialMessage, setInitialMessage] = useState<string | undefined>(undefined);
  const { pathname } = useLocation();
  const { isMobile, setOpen, state } = useSidebar();
  const [openGroups, setOpenGroups] = useState<Partial<Record<string, boolean>>>(() =>
    getOpenGroups(pathname)
  );

  useEffect(() => {
    const activeGroups = items.filter(
      (item) => hasNestedItems(item) && isItemActive(pathname, item)
    );

    if (activeGroups.length === 0) {
      return;
    }

    setOpenGroups((currentGroups) => {
      let nextGroups = currentGroups;

      activeGroups.forEach((item) => {
        if (nextGroups[item.title] !== true) {
          nextGroups = {
            ...nextGroups,
            [item.title]: true,
          };
        }
      });

      return nextGroups;
    });
  }, [pathname]);

  const handleOpenChat = (message?: string) => {
    setInitialMessage(message);
    setIsChatOpen(true);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
    setInitialMessage(undefined);
  };

  const handleGroupLinkClick = (item: SidebarItem) => {
    if (hasNestedItems(item)) {
      setOpenGroups((currentGroups) => {
        if (currentGroups[item.title]) {
          return currentGroups;
        }

        return {
          ...currentGroups,
          [item.title]: true,
        };
      });
    }

    if (!isMobile && state === 'collapsed') {
      setOpen(true);
    }
  };

  return (
    <>
      <Sidebar>
        <SidebarHeader>
          <figure
            className="
              relative px-2 pt-5 pb-1
              *:h-7
            "
          >
            <CortexaLogo size="sm" />
          </figure>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup className="p-3 font-medium">
            <SidebarGroupContent>
              <SidebarMenu className="gap-y-0.5">
                {items.map((item) => {
                  const itemIsActive = isItemActive(pathname, item);
                  const itemIsDirectlyActive = isItemDirectlyActive(pathname, item);

                  return hasNestedItems(item) ? (
                    <Collapsible
                      key={item.title}
                      open={openGroups[item.title] ?? false}
                      onOpenChange={(open) => {
                        setOpenGroups((currentGroups) => ({
                          ...currentGroups,
                          [item.title]: open,
                        }));
                      }}
                      className="group/collapsible"
                      render={<SidebarMenuItem className="relative" />}
                    >
                      <SidebarMenuButton
                        className="h-10 px-2.5"
                        size="lg"
                        tooltip={item.title}
                        isActive={isRouteActive(pathname, item.url)}
                        render={<Link to={item.url} />}
                        onClick={() => {
                          handleGroupLinkClick(item);
                        }}
                      >
                        <item.icon
                          className={itemIsDirectlyActive ? 'text-primary' : undefined}
                          weight={itemIsDirectlyActive ? 'duotone' : undefined}
                        />
                        <span>{item.title}</span>
                      </SidebarMenuButton>

                      <CollapsibleTrigger
                        render={
                          <SidebarMenuAction
                            className="
                              hover:bg-background-3
                              aria-expanded:rotate-90
                            "
                          />
                        }
                      >
                        <CaretRightIcon weight="bold" />
                        <span className="sr-only">Toggle {item.title}</span>
                      </CollapsibleTrigger>

                      <CollapsibleContent>
                        <SidebarMenuSub className="gap-y-0.5 border-none">
                          {item.items.map((subItem) => (
                            <SidebarMenuSubItem key={subItem.title}>
                              <SidebarMenuSubButton
                                className="h-9 px-2.5"
                                isActive={isRouteActive(pathname, subItem.url)}
                                render={<Link to={subItem.url} />}
                              >
                                <span>{subItem.title}</span>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </Collapsible>
                  ) : (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        className="h-10 px-2.5"
                        tooltip={item.title}
                        isActive={itemIsActive}
                        render={<Link to={item.url} />}
                        onClick={() => {
                          handleGroupLinkClick(item);
                        }}
                      >
                        <item.icon
                          className={itemIsActive ? 'text-primary' : undefined}
                          weight={itemIsActive ? 'duotone' : undefined}
                        />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter className="p-4">
          <AppSidebarAIChatButton onOpenChat={handleOpenChat} />
          <AppSidebarOnboarding title="Get Started" items={getStartedItems} />
        </SidebarFooter>
      </Sidebar>

      {/* AI Chat Modal */}
      <AIChatModal isOpen={isChatOpen} onClose={handleCloseChat} initialMessage={initialMessage} />
    </>
  );
}
